import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Plus, Pencil, Trash2 } from 'lucide-react';

import {
  getMyLanguages, createMyLanguage, updateMyLanguage, deleteMyLanguage,
  getMyEducations, createMyEducation, updateMyEducation, deleteMyEducation,
  getMyWorkExperiences, createMyWorkExperience, updateMyWorkExperience, deleteMyWorkExperience,
} from '../../services/resumeService';
import { formatDate } from '../../utils/Utils';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Button from '../../components/ui/Button';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PageTitle = styled.h1`
  font-family: 'Nunito Sans', sans-serif;
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`;

const Section = styled.div`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  cursor: pointer;
  user-select: none;
  &:hover { background: #FAFBFC; }
`;

const SectionTitle = styled.h2`
  font-family: 'Nunito Sans', sans-serif;
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionBody = styled.div`
  padding: 0 20px 20px;
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Card = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: ${({ theme }) => theme.colors.bgColor};
  border: 1px solid #E8ECF0;
  border-radius: 6px;
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CardTitle = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CardSub = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #A0A8B4;
`;

const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #E0E4E8;
  border-radius: 4px;
  background: #fff;
  color: #004242;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: ${({ $danger }) => $danger ? '#ef4444' : '#0291EF'}; color: ${({ $danger }) => $danger ? '#ef4444' : '#0291EF'}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $status }) =>
    $status === 'approved' ? '#10b98115' :
    $status === 'rejected' ? '#ef444415' :
    '#f59e0b15'};
  color: ${({ $status }) =>
    $status === 'approved' ? '#10b981' :
    $status === 'rejected' ? '#ef4444' :
    '#f59e0b'};
  margin-left: 8px;
`;

const EmptyText = styled.p`
  color: #A0A8B4;
  font-size: 13px;
  text-align: center;
  padding: 16px;
`;

export default function MyResumePage() {
  const { t, i18n } = useTranslation();

  // Section collapsed state
  const [openSections, setOpenSections] = useState({ languages: true, education: true, workExperience: true });

  // Data
  const [languages, setLanguages] = useState([]);
  const [educations, setEducations] = useState([]);
  const [workExperiences, setWorkExperiences] = useState([]);

  // Drawer state
  const [drawerType, setDrawerType] = useState(null); // 'language' | 'education' | 'workExperience'
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, type: null, id: null, name: '' });
  const [deleting, setDeleting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [langRes, eduRes, expRes] = await Promise.all([
        getMyLanguages({ pageSize: 100 }),
        getMyEducations({ pageSize: 100 }),
        getMyWorkExperiences({ pageSize: 100 }),
      ]);
      setLanguages(langRes.data?.data?.data || langRes.data?.data || []);
      setEducations(eduRes.data?.data?.data || eduRes.data?.data || []);
      setWorkExperiences(expRes.data?.data?.data || expRes.data?.data || []);
    } catch { /* interceptor */ }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleSection = useCallback((key) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleAdd = useCallback((type) => {
    setDrawerType(type);
    setSelectedItem(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((type, item) => {
    setDrawerType(type);
    setSelectedItem({ ...item });
    setDrawerOpen(true);
  }, []);

  const handleDeleteClick = useCallback((type, item) => {
    setDeleteConfirm({ isOpen: true, type, id: item.id, name: item.language || item.institution || item.title || '' });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      if (deleteConfirm.type === 'language') await deleteMyLanguage(deleteConfirm.id);
      else if (deleteConfirm.type === 'education') await deleteMyEducation(deleteConfirm.id);
      else if (deleteConfirm.type === 'workExperience') await deleteMyWorkExperience(deleteConfirm.id);
      await fetchAll();
    } catch { /* interceptor */ }
    setDeleteConfirm({ isOpen: false, type: null, id: null, name: '' });
    setDeleting(false);
  }, [deleteConfirm, fetchAll]);

  const handleSave = useCallback(async (formData) => {
    try {
      if (drawerType === 'language') {
        selectedItem ? await updateMyLanguage(selectedItem.id, formData) : await createMyLanguage(formData);
      } else if (drawerType === 'education') {
        selectedItem ? await updateMyEducation(selectedItem.id, formData) : await createMyEducation(formData);
      } else if (drawerType === 'workExperience') {
        selectedItem ? await updateMyWorkExperience(selectedItem.id, formData) : await createMyWorkExperience(formData);
      }
      await fetchAll();
      setDrawerOpen(false);
      return true;
    } catch { return false; }
  }, [drawerType, selectedItem, fetchAll]);

  const proficiencyOptions = useMemo(() => [
    { id: 'beginner', name: t('resume.beginner') },
    { id: 'elementary', name: t('resume.elementary') },
    { id: 'intermediate', name: t('resume.intermediate') },
    { id: 'upperIntermediate', name: t('resume.upperIntermediate') },
    { id: 'advanced', name: t('resume.advanced') },
    { id: 'native', name: t('resume.native') },
  ], [t]);

  const educationLevelOptions = useMemo(() => [
    { id: 'highSchool', name: t('resume.highSchool') },
    { id: 'associate', name: t('resume.associate') },
    { id: 'bachelor', name: t('resume.bachelor') },
    { id: 'master', name: t('resume.master') },
    { id: 'doctorate', name: t('resume.doctorate') },
    { id: 'other', name: t('resume.other') },
  ], [t]);

  const educationStatusOptions = useMemo(() => [
    { id: 'graduated', name: t('resume.graduated') },
    { id: 'ongoing', name: t('resume.ongoing') },
    { id: 'dropped', name: t('resume.dropped') },
  ], [t]);

  const languageSchema = useMemo(() => [
    { name: 'language', label: t('resume.language'), type: 'languageSelect', grid: 12, required: true },
    { name: 'isNative', label: t('resume.isNative'), type: 'checkbox', grid: 12 },
    { name: 'spokenProficiency', label: t('resume.spokenProficiency'), type: 'select', grid: 6, options: proficiencyOptions, idKey: 'id', labelKey: 'name' },
    { name: 'writtenProficiency', label: t('resume.writtenProficiency'), type: 'select', grid: 6, options: proficiencyOptions, idKey: 'id', labelKey: 'name' },
    { name: 'dateAcquired', label: t('resume.dateAcquired'), type: 'date', grid: 12 },
  ], [t, proficiencyOptions]);

  const educationSchema = useMemo(() => [
    { name: 'educationLevel', label: t('resume.educationLevel'), type: 'select', grid: 6, required: true, options: educationLevelOptions, idKey: 'id', labelKey: 'name' },
    { name: 'educationStatus', label: t('resume.educationStatus'), type: 'select', grid: 6, options: educationStatusOptions, idKey: 'id', labelKey: 'name' },
    { name: 'institution', label: t('resume.institution'), type: 'text', grid: 12, required: true },
    { name: 'faculty', label: t('resume.faculty'), type: 'text', grid: 12 },
    { name: 'graduationDate', label: t('resume.graduationDate'), type: 'date', grid: 12 },
  ], [t, educationLevelOptions, educationStatusOptions]);

  const workExperienceSchema = useMemo(() => [
    { name: 'title', label: t('resume.jobTitle'), type: 'text', grid: 12, required: true },
    { name: 'organization', label: t('resume.organization'), type: 'text', grid: 12, required: true },
    { name: 'employmentDate', label: t('resume.employmentDate'), type: 'date', grid: 6 },
    { name: 'endDate', label: t('resume.endDate'), type: 'date', grid: 6 },
    { name: 'location', label: t('resume.location'), type: 'text', grid: 12 },
  ], [t]);

  const getDrawerSchema = () => {
    if (drawerType === 'language') return languageSchema;
    if (drawerType === 'education') return educationSchema;
    if (drawerType === 'workExperience') return workExperienceSchema;
    return [];
  };

  const getDrawerTitle = () => {
    if (drawerType === 'language') return selectedItem ? t('certification.edit') : t('resume.addLanguage');
    if (drawerType === 'education') return selectedItem ? t('certification.edit') : t('resume.addEducation');
    if (drawerType === 'workExperience') return selectedItem ? t('certification.edit') : t('resume.addExperience');
    return '';
  };

  const statusLabels = useMemo(() => ({
    waiting: t('certification.waiting'),
    approved: t('certification.approved'),
    rejected: t('certification.rejected'),
  }), [t]);

  const canEditItem = (item) => !item.status || item.status === 'waiting' || item.status === 'rejected';
  const canDeleteItem = (item) => !item.status || item.status === 'waiting';

  return (
    <Container>
      <PageTitle>{t('resume.myResume')}</PageTitle>

      {/* Languages Section */}
      <Section>
        <SectionHeader onClick={() => toggleSection('languages')}>
          <SectionTitle>{t('resume.languages')}</SectionTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button onClick={(e) => { e.stopPropagation(); handleAdd('language'); }} style={{ padding: '6px 14px', fontSize: 12 }}>
              <Plus size={14} style={{ marginRight: 4 }} />{t('resume.addLanguage')}
            </Button>
            {openSections.languages ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>
        </SectionHeader>
        {openSections.languages && (
          <SectionBody>
            {languages.length === 0 ? (
              <EmptyText>{t('resume.noLanguages')}</EmptyText>
            ) : (
              <CardList>
                {languages.map((item) => (
                  <Card key={item.id}>
                    <CardInfo>
                      <CardTitle>
                        {item.language}
                        {item.status && <StatusBadge $status={item.status}>{statusLabels[item.status] || item.status}</StatusBadge>}
                      </CardTitle>
                      <CardSub>
                        {item.isNative ? t('resume.native') : `${t('resume.spokenProficiency')}: ${item.spokenProficiency || '-'} | ${t('resume.writtenProficiency')}: ${item.writtenProficiency || '-'}`}
                      </CardSub>
                    </CardInfo>
                    <CardActions>
                      <IconBtn onClick={() => handleEdit('language', item)} disabled={!canEditItem(item)}>
                        <Pencil size={14} />
                      </IconBtn>
                      <IconBtn $danger onClick={() => handleDeleteClick('language', item)} disabled={!canDeleteItem(item)}>
                        <Trash2 size={14} />
                      </IconBtn>
                    </CardActions>
                  </Card>
                ))}
              </CardList>
            )}
          </SectionBody>
        )}
      </Section>

      {/* Education Section */}
      <Section>
        <SectionHeader onClick={() => toggleSection('education')}>
          <SectionTitle>{t('resume.education')}</SectionTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button onClick={(e) => { e.stopPropagation(); handleAdd('education'); }} style={{ padding: '6px 14px', fontSize: 12 }}>
              <Plus size={14} style={{ marginRight: 4 }} />{t('resume.addEducation')}
            </Button>
            {openSections.education ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>
        </SectionHeader>
        {openSections.education && (
          <SectionBody>
            {educations.length === 0 ? (
              <EmptyText>{t('resume.noEducations')}</EmptyText>
            ) : (
              <CardList>
                {educations.map((item) => (
                  <Card key={item.id}>
                    <CardInfo>
                      <CardTitle>
                        {item.institution}
                        {item.status && <StatusBadge $status={item.status}>{statusLabels[item.status] || item.status}</StatusBadge>}
                      </CardTitle>
                      <CardSub>
                        {item.educationLevel && (educationLevelOptions.find(o => o.id === item.educationLevel)?.name || item.educationLevel)}
                        {item.faculty && ` - ${item.faculty}`}
                        {item.graduationDate && ` | ${formatDate(item.graduationDate, i18n.language)}`}
                      </CardSub>
                    </CardInfo>
                    <CardActions>
                      <IconBtn onClick={() => handleEdit('education', item)} disabled={!canEditItem(item)}>
                        <Pencil size={14} />
                      </IconBtn>
                      <IconBtn $danger onClick={() => handleDeleteClick('education', item)} disabled={!canDeleteItem(item)}>
                        <Trash2 size={14} />
                      </IconBtn>
                    </CardActions>
                  </Card>
                ))}
              </CardList>
            )}
          </SectionBody>
        )}
      </Section>

      {/* Work Experience Section */}
      <Section>
        <SectionHeader onClick={() => toggleSection('workExperience')}>
          <SectionTitle>{t('resume.workExperience')}</SectionTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button onClick={(e) => { e.stopPropagation(); handleAdd('workExperience'); }} style={{ padding: '6px 14px', fontSize: 12 }}>
              <Plus size={14} style={{ marginRight: 4 }} />{t('resume.addExperience')}
            </Button>
            {openSections.workExperience ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>
        </SectionHeader>
        {openSections.workExperience && (
          <SectionBody>
            {workExperiences.length === 0 ? (
              <EmptyText>{t('resume.noExperiences')}</EmptyText>
            ) : (
              <CardList>
                {workExperiences.map((item) => (
                  <Card key={item.id}>
                    <CardInfo>
                      <CardTitle>
                        {item.title}
                        {item.status && <StatusBadge $status={item.status}>{statusLabels[item.status] || item.status}</StatusBadge>}
                      </CardTitle>
                      <CardSub>
                        {item.organization}
                        {item.employmentDate && ` | ${formatDate(item.employmentDate, i18n.language)}`}
                        {item.endDate && ` - ${formatDate(item.endDate, i18n.language)}`}
                        {item.location && ` | ${item.location}`}
                      </CardSub>
                    </CardInfo>
                    <CardActions>
                      <IconBtn onClick={() => handleEdit('workExperience', item)} disabled={!canEditItem(item)}>
                        <Pencil size={14} />
                      </IconBtn>
                      <IconBtn $danger onClick={() => handleDeleteClick('workExperience', item)} disabled={!canDeleteItem(item)}>
                        <Trash2 size={14} />
                      </IconBtn>
                    </CardActions>
                  </Card>
                ))}
              </CardList>
            )}
          </SectionBody>
        )}
      </Section>

      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={getDrawerTitle()}
        schema={getDrawerSchema()}
        initialValues={selectedItem}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('certification.delete')}
        message={t('approvalFlow.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, type: null, id: null, name: '' })}
        loading={deleting}
      />
    </Container>
  );
}
